import { useState, useEffect, useRef } from "react";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./Button";
import AuthModal from "../modals/AuthModal";
import ProfileModal from "../modals/ProfileModal";
import SettingsModal from "../modals/SettingsModal";
import LegalModal from "../modals/LegalModal";
import { LogOut, User, Settings, ChevronDown, History as HistoryIcon } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const DropdownItem = ({ icon: Icon, label, onClick, className }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-lg transition-colors w-full text-left uppercase tracking-wider active:bg-zinc-800/80 touch-none ${className || ""}`}
  >
    {Icon && <Icon size={12} className="sm:w-[14px] sm:h-[14px]" />}
    {label}
  </button>
);

let __diagInstanceCounter = 0;
export default function Header() {
  const { user } = useAuth();
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    instanceIdRef.current = ++__diagInstanceCounter;
    console.log('[DIAG] Header NEW INSTANCE mounted, id=', instanceIdRef.current);
  }
  console.log('[DIAG] Header render, id=', instanceIdRef.current, 'user=', user);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState("login");
  const [authModalMessage, setAuthModalMessage] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [legalModalView, setLegalModalView] = useState("terms");
  const [logoError, setLogoError] = useState(false);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.open) {
      if (['login', 'register'].includes(location.state.open)) {
        openModal(location.state.open, location.state.message);
      } else if (['terms', 'privacy', 'security'].includes(location.state.open)) {
        setLegalModalView(location.state.open);
        setIsLegalModalOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const openModal = (view, message = "") => {
    console.log('[DIAG] openModal called', view);
    setAuthModalView(view);
    setAuthModalMessage(message);
    setAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    console.log('[DIAG] closeAuthModal called', new Error().stack);
    setAuthModalOpen(false);
    setAuthModalMessage("");
  };

  useEffect(() => {
    setLogoError(false);
  }, []);

  return (
    <>
      {/* Header bar is now slim (py-2 to py-3) */}
      <header className="relative z-50 w-full px-3 sm:px-6 md:px-8 py-2 sm:py-3 flex justify-between items-center max-w-7xl mx-auto pointer-events-auto bg-transparent overflow-visible">
        
        {/* Logo Container - Absolute positioned to prevent stretching the header */}
        <div className="relative flex items-center overflow-visible pointer-events-none">
          <Link
            to="/"
            className="relative group transition-all duration-300 pointer-events-auto rounded-lg flex-shrink-0"
            aria-label="TypeBattle - Go to homepage"
          >
            {!logoError ? (
              <img
                src="/typebattle-logo.png"
                alt="TypeBattle Logo"
                width="1264"
                height="842"
                /* Image stays large, but doesn't push the header height */
                className="h-16 sm:h-20 md:h-24 w-auto object-contain transition-all duration-500 group-hover:scale-105 will-change-transform select-none drop-shadow-2xl"
                onError={() => setLogoError(true)}
                loading="eager"
                draggable="false"
              />
            ) : (
              <div className="flex items-center whitespace-nowrap">
                <span className="text-lg sm:text-xl font-black italic text-white group-hover:text-indigo-400 transition-colors duration-300 select-none">
                  TYPE<span className="text-indigo-400 group-hover:text-white">BATTLE</span>
                </span>
              </div>
            )}
          </Link>
        </div>

        {/* Account Area - Centered vertically in the slim bar */}
        <div className="flex items-center gap-3 sm:gap-6 pointer-events-auto flex-shrink-0">
          {user ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 sm:gap-3 group outline-none active:scale-95 transition-transform"
              >
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-300 font-black transition-colors">
                    Account
                  </span>
                </div>

                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 group-hover:text-blue-400 group-hover:border-blue-500/50 transition-all shadow-lg flex-shrink-0">
                  <User size={16} />
                </div>

                <ChevronDown
                  size={11}
                  className={`text-zinc-600 transition-transform duration-300 hidden sm:block ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200 origin-top-right z-50">
                  <div className="p-3 sm:p-4 border-b border-zinc-800 bg-zinc-900/50">
                    <p className="text-zinc-200 text-xs sm:text-sm font-bold truncate">
                      {user.displayName || "Player"}
                    </p>
                    <p className="text-[9px] sm:text-[10px] text-zinc-500 font-mono truncate">
                      {user.email}
                    </p>
                  </div>

                  <div className="p-1 flex flex-col gap-0.5">
                    <DropdownItem
                      icon={User}
                      label="Profile"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setIsProfileModalOpen(true);
                      }}
                    />
                    <DropdownItem
                      icon={Settings}
                      label="Settings"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setIsSettingsModalOpen(true);
                      }}
                    />
                    <DropdownItem
                      icon={HistoryIcon}
                      label="History"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate("/history");
                      }}
                    />
                  </div>

                  <div className="p-1 border-t border-zinc-800">
                    <DropdownItem
                      icon={LogOut}
                      label="Logout"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        handleLogout();
                      }}
                      className="hover:text-red-400 hover:bg-red-500/10"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 sm:gap-6 flex-shrink-0">
              <button
                onClick={() => openModal("login")}
                className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-200 font-black transition-colors active:scale-95"
              >
                Login
              </button>
              <Button
                onClick={() => openModal("register")}
                variant="primary-outline"
                size="sm"
                className="px-3 sm:px-5 tracking-[0.2em] !font-black text-[9px] sm:text-xs active:scale-95"
              >
                Register
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Modals remain the same */}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} initialView={authModalView} message={authModalMessage} />
      {isProfileModalOpen && <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} user={user} />}
      <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} user={user} />
      <LegalModal isOpen={isLegalModalOpen} onClose={() => setIsLegalModalOpen(false)} view={legalModalView} />
    </>
  );
}